export type PrivacyTier = 'anon' | 'private' | 'tee';

export interface PrivacyMeta {
  /** Known tier (drives badge styling); null for an unrecognized label. */
  readonly tier: PrivacyTier | null;
  readonly label: string;
  readonly definition: string;
}

const META: Record<PrivacyTier, PrivacyMeta> = {
  tee: {
    tier: 'tee',
    label: 'TEE',
    definition:
      'Trusted Execution Environment. Inference runs inside a hardware-isolated enclave operated by external TEE providers. GPU providers cannot access your prompts. Verified by remote attestation.',
  },
  private: {
    tier: 'private',
    label: 'Private',
    definition:
      'Privacy is enforced by contractual commitments — no prompt or response is stored.',
  },
  anon: {
    tier: 'anon',
    label: 'Anon',
    definition:
      'Your identity is obscured from the provider, but you should assume the provider is storing your content.',
  },
};

const PRIVACY_TAG_RE = /^privacy:\s*(.+)$/iu;

/**
 * Privacy badge for a model, read from the gateway's admin-curated catalog
 * tags (`Privacy: <label>`, matched case-insensitively; the first one wins).
 * Known labels (TEE / Private / Anon) carry their tooltip definitions and a
 * tier for badge styling; an unrecognized label still renders verbatim
 * (untiered, no tooltip) so a new tier ships without a UI release. No
 * Privacy tag ⇒ no badge — including the whole catalog when the gateway
 * served a fallback source that carries no tags.
 */
export function privacyFromTags(
  tags: readonly string[] | undefined,
): PrivacyMeta | null {
  if (tags == null) return null;
  for (const tag of tags) {
    const match = PRIVACY_TAG_RE.exec(tag.trim());
    if (match == null) continue;
    const label = match[1]!.trim();
    const key = label.toLowerCase();
    if (key === 'tee' || key === 'private' || key === 'anon') {
      return META[key];
    }
    return { tier: null, label, definition: '' };
  }
  return null;
}
